import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import { Plus, X, Edit2, Trash2, Eye, Search, Upload, Download, Paperclip } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import DateInput from '../components/ui/DateInput';
import { saveStyledXlsx } from '../lib/excelStyle';

// ═══════════════════════════════════════════════════════════════════
//  ASSETS & CONSUMABLES
//  Needs assets_setup.sql (tables, stock view, private bucket "asset-docs").
// ═══════════════════════════════════════════════════════════════════
const BUCKET = 'asset-docs';
const DEPARTMENTS = ['Training', 'Construction', 'Inspection', 'Admin'];
const ASSET_CATEGORIES = ['Computer & IT', 'Furniture', 'Machinery & Equipment', 'Tools', 'Lab / Workshop Equipment', 'Inspection / NDT Equipment', 'Safety Equipment', 'Vehicle', 'Electrical / AC', 'Office Equipment', 'Other'];
const STATUSES = ['Working', 'Under repair', 'Damaged', 'Idle', 'Lost', 'Disposed'];
const STATUS_COLOR = { 'Working': ['#dcfce7', '#166534'], 'Under repair': ['#fef3c7', '#92400e'], 'Damaged': ['#fee2e2', '#991b1b'], 'Idle': ['#e5e7eb', '#374151'], 'Lost': ['#fee2e2', '#991b1b'], 'Disposed': ['#e5e7eb', '#6b7280'] };
const MAINT_TYPES = ['Service', 'Repair', 'AMC', 'Calibration', 'Other'];
const CONS_CATEGORIES = ['Stationery', 'Cleaning', 'Workshop / Lab', 'Printer & Toner', 'Safety', 'Electrical', 'Pantry', 'Other'];
const UNITS = ['pcs', 'box', 'pack', 'set', 'kg', 'litre', 'metre', 'roll', 'ream', 'bottle'];
const PAY_MODES_FALLBACK = ['Cash', 'EKM FDO', 'EKM Admin', 'TVM FDO', 'TVM Admin', 'SBI', 'HDFC Vazhakkala'];
const ACC_COMPANIES = ['ARMSTECH', 'SLCCS'];
const PAGE_SIZE = 50;
const EXPIRY_SOON = 30;           // days: warranty / service reminders
const REPLACE_RATIO = 0.5;        // repair-or-replace flag when lifetime repairs exceed 50% of price

// ── helpers ───────────────────────────────────────────────────────
const num = v => { const n = Number(v); return isFinite(n) ? n : 0; };
const inr = v => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const pad2 = n => String(n).padStart(2, '0');
const dstr = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayStr = () => dstr(new Date());
const fmtDate = d => { if (!d) return '—'; try { return new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch (e) { return d; } };
const daysTo = d => d ? Math.round((new Date(String(d).slice(0, 10) + 'T00:00:00') - new Date(todayStr() + 'T00:00:00')) / 86400000) : null;
const addYears = (d, yrs) => {
  if (!d || !(num(yrs) > 0)) return '';
  const dt = new Date(String(d).slice(0, 10) + 'T00:00:00');
  dt.setMonth(dt.getMonth() + Math.round(num(yrs) * 12));
  return dstr(dt);
};
// straight-line depreciation → current book value of the whole row
const bookValue = a => {
  const cost = num(a.purchase_amount), life = num(a.useful_life_years);
  if (!a.purchase_date || !(life > 0)) return null;
  if (a.status === 'Disposed' || a.status === 'Lost') return 0;
  const salvage = Math.min(cost, num(a.salvage_value));
  const used = (new Date() - new Date(String(a.purchase_date).slice(0, 10) + 'T00:00:00')) / (365.25 * 86400000);
  return Math.max(salvage, cost - (cost - salvage) * Math.min(1, Math.max(0, used) / life));
};
const isMissingTable = e => !!e && (e.code === '42P01' || e.code === 'PGRST205' || /does not exist|schema cache/i.test(e.message || ''));
async function whoAmI() {
  try { const { data } = await supabase.auth.getUser(); return data?.user?.email || null; } catch (e) { return null; }
}
async function openAttachment(a) {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(a.path, 300);
  if (error) { toast.error('Cannot open file: ' + error.message); return; }
  window.open(data.signedUrl, '_blank', 'noopener');
}
async function removeFiles(list) {
  const paths = (list || []).map(a => a.path).filter(Boolean);
  if (paths.length) { try { await supabase.storage.from(BUCKET).remove(paths); } catch (e) { /* ignore */ } }
}

// Puts a purchase / repair bill into the existing Accounts expense approval queue (it does NOT post to the books by itself)
async function sendToAccounts({ date, amount, category, party, reference, description, company, payMode }) {
  const { data: u } = await supabase.auth.getUser();
  const { data: st } = await supabase.from('staff').select('id').eq('user_id', u?.user?.id).maybeSingle();
  if (!st?.id) throw new Error('Your login is not linked to a staff record');
  const { data, error } = await supabase.from('expense_entries').insert({
    date: date || todayStr(), type: 'Cash Out', category, payment_mode: payMode || 'Cash',
    party_name: party || null, amount: num(amount), reference: reference || null,
    description: description || null, entry_type: 'site_expense', is_reimbursement: false,
    staff_id: st.id, company: company || 'ARMSTECH', status: 'submitted',
  }).select('id').single();
  if (error) throw error;
  return data.id;
}

// ── small UI pieces ───────────────────────────────────────────────
const FG = ({ label, children, full, style = {} }) => (
  <div className={`form-group${full ? ' full' : ''}`} style={style}>
    <label style={{ fontSize: 11 }}>{label}</label>{children}
  </div>
);
const KV = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
    <div style={{ width: 140, fontWeight: 600, fontSize: 12, color: 'var(--text2)', flexShrink: 0 }}>{label}</div>
    <div style={{ fontSize: 13, minWidth: 0, wordBreak: 'break-word' }}>{children}</div>
  </div>
);
const SH = ({ children, mt = 14 }) => (
  <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.6px', margin: `${mt}px 0 8px`, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>{children}</div>
);
const StatusBadge = ({ status }) => {
  const c = STATUS_COLOR[status] || ['#e5e7eb', '#374151'];
  return <span style={{ background: c[0], color: c[1], borderRadius: 10, padding: '2px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{status || '—'}</span>;
};
const Chip = ({ children, bg = '#e0e7ff', color = '#3730a3' }) => (
  <span style={{ display: 'inline-block', background: bg, color, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{children}</span>
);
function Modal({ title, onClose, children, width = 820 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ maxWidth: width, maxHeight: '94vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
function Stat({ label, value, sub, color = '#00756a', bg = '#fff', onClick }) {
  return (
    <div className="stat-card" onClick={onClick} style={{ borderLeft: `4px solid ${color}`, background: bg, cursor: onClick ? 'pointer' : 'default' }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color, fontSize: 24 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: 'var(--text3)' }}>{sub}</div> : null}
    </div>
  );
}
function Pager({ page, setPage, total }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  return (
    <div className="pagination">
      <button className="page-btn" disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Prev</button>
      <span style={{ fontSize: 12, color: 'var(--text2)' }}>Page {page} of {pages} · {total} rows</span>
      <button className="page-btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next ›</button>
    </div>
  );
}

// Upload / list / open attachments (private bucket "asset-docs")
function Attachments({ value, onChange, folder, editable }) {
  const [busy, setBusy] = useState(false);
  const list = value || [];
  async function onPick(e) {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: larger than 10 MB`); continue; }
      const path = `${folder}/${Date.now()}_${f.name.replace(/[^\w.-]+/g, '_')}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, f, { contentType: f.type || undefined });
      if (error) { toast.error(`${f.name}: ${error.message}`); continue; }
      added.push({ path, name: f.name, size: f.size, at: new Date().toISOString() });
    }
    if (added.length) onChange([...list, ...added]);
    setBusy(false);
  }
  return (
    <div>
      {list.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>No files attached</div>}
      {list.map((a, i) => (
        <div key={a.path || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
          <Paperclip size={12} style={{ color: 'var(--text3)' }} />
          <a href="#open" onClick={e => { e.preventDefault(); openAttachment(a); }} style={{ color: 'var(--accent)', fontWeight: 600 }}>{a.name}</a>
          <span style={{ color: 'var(--text3)' }}>{a.size ? (a.size / 1024).toFixed(0) + ' KB' : ''}</span>
          {editable && <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Remove" onClick={() => onChange(list.filter((_, j) => j !== i))}><X size={12} /></button>}
        </div>
      ))}
      {editable && (
        <label className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: busy ? 'wait' : 'pointer', marginTop: 4 }}>
          <Upload size={13} /> {busy ? 'Uploading…' : 'Attach invoice / warranty card / photo (max 10 MB)'}
          <input type="file" multiple style={{ display: 'none' }} disabled={busy} onChange={onPick} />
        </label>
      )}
    </div>
  );
}

// Optional "send to Accounts" block shared by purchases, repairs and stock-in
function AccountsBlock({ enabled, onToggle, company, onCompany, payMode, onPayMode, payModes, already, label }) {
  if (already) return <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 8 }}>✓ Already sent to Accounts</div>;
  return (
    <div style={{ marginTop: 10, padding: 10, border: '1px dashed var(--border2)', borderRadius: 8, background: 'var(--surface)' }}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, cursor: 'pointer' }}>
        <input type="checkbox" style={{ width: 'auto' }} checked={enabled} onChange={e => onToggle(e.target.checked)} />
        <b>Send to Accounts</b> <span style={{ color: 'var(--text3)' }}>({label || 'goes to the Expense Inbox for approval, not straight into the books'})</span>
      </label>
      {enabled && (
        <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <FG label="Company"><select value={company} onChange={e => onCompany(e.target.value)}>{ACC_COMPANIES.map(c => <option key={c}>{c}</option>)}</select></FG>
          <FG label="Payment mode"><select value={payMode} onChange={e => onPayMode(e.target.value)}>{payModes.map(m => <option key={m}>{m}</option>)}</select></FG>
        </div>
      )}
    </div>
  );
}

// ── per-asset maintenance summary (cost, last service, next due) ──
function summarizeMaint(maintRows) {
  const m = {};
  const sorted = [...(maintRows || [])].sort((a, b) => String(a.maint_date || '').localeCompare(String(b.maint_date || '')));
  sorted.forEach(r => {
    const s = m[r.asset_id] || (m[r.asset_id] = { cost: 0, count: 0, last: null, nextDue: null });
    s.cost += num(r.cost); s.count += 1; s.last = r.maint_date || s.last; s.nextDue = r.next_due || null;
  });
  return m;
}

// ═══════════════════════════════════════════════════════════════════
//  ASSET FORM (add / edit)
// ═══════════════════════════════════════════════════════════════════
const blankAsset = () => ({
  name: '', category: '', department: '', location_id: '', specific_place: '', make_model: '', serial_no: '',
  tracking: 'individual', quantity: 1, units: 1, serials: '',
  purchase_date: todayStr(), unit_price: '', gst_amount: '', vendor_id: '', shop_name: '', invoice_no: '', payment_mode: '',
  warranty_years: '', warranty_expiry: '', amc_details: '', useful_life_years: '', salvage_value: '',
  status: 'Working', handler_staff_id: '', used_for: '', notes: '', attachments: [],
  disposal_date: '', disposal_value: '', disposal_reason: '',
});
function assetToForm(a) {
  const group = a.tracking === 'group';
  const q = Math.max(1, num(a.quantity) || 1);
  return {
    ...blankAsset(), ...a,
    quantity: q, units: 1, serials: '',
    unit_price: group ? Math.round(num(a.purchase_amount) / q * 100) / 100 : num(a.purchase_amount),
    salvage_value: group ? Math.round(num(a.salvage_value) / q * 100) / 100 : num(a.salvage_value),
    purchase_date: a.purchase_date || '', warranty_expiry: a.warranty_expiry || '', disposal_date: a.disposal_date || '',
    warranty_years: a.warranty_years ?? '', useful_life_years: a.useful_life_years ?? '', gst_amount: a.gst_amount ?? '',
    disposal_value: a.disposal_value ?? '', vendor_id: a.vendor_id || '', location_id: a.location_id || '',
    handler_staff_id: a.handler_staff_id || '', attachments: a.attachments || [],
  };
}

function AssetForm({ ctx, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => editing ? assetToForm(editing) : blankAsset());
  const [saving, setSaving] = useState(false);
  const [sendAcc, setSendAcc] = useState(false);
  const [accCompany, setAccCompany] = useState('ARMSTECH');
  const [accMode, setAccMode] = useState('Cash');
  const sf = (k, v) => setF(p => ({ ...p, [k]: v }));
  const group = f.tracking === 'group';
  const multi = !editing && !group;
  const rows = group ? Math.max(1, Math.floor(num(f.quantity)) || 1) : (multi ? Math.max(1, Math.floor(num(f.units)) || 1) : 1);
  const total = num(f.unit_price) * rows;
  const autoExpiry = addYears(f.purchase_date, f.warranty_years);
  const editableLocs = ctx.locations.filter(l => l.is_active !== false && ctx.canEditLoc(l.id));

  async function save() {
    if (!f.name.trim()) return toast.error('Asset name is required');
    if (!f.department) return toast.error('Choose the department');
    if (!f.location_id) return toast.error('Choose the location');
    if (!ctx.canEditLoc(f.location_id)) return toast.error('You can only add or edit assets for your own location');
    if (num(f.unit_price) < 0) return toast.error('Amount cannot be negative');
    if (sendAcc && !(total > 0)) return toast.error('Enter the purchase amount to send it to Accounts');
    setSaving(true);
    try {
      const me = await whoAmI();
      const staffRow = ctx.staff.find(s => String(s.id) === String(f.handler_staff_id));
      const vendor = ctx.vendors.find(v => v.id === f.vendor_id);
      const unitGst = multi ? num(f.gst_amount) / rows : num(f.gst_amount);
      const core = {
        name: f.name.trim(), category: f.category || null, department: f.department, location_id: f.location_id,
        specific_place: f.specific_place || null, make_model: f.make_model || null,
        tracking: f.tracking, quantity: group ? rows : 1,
        purchase_date: f.purchase_date || null,
        purchase_amount: num(f.unit_price) * (group ? rows : 1),
        gst_amount: unitGst || 0,
        vendor_id: f.vendor_id || null, shop_name: f.shop_name || (vendor ? vendor.name : null),
        invoice_no: f.invoice_no || null, payment_mode: f.payment_mode || null,
        warranty_years: f.warranty_years === '' ? null : num(f.warranty_years),
        warranty_expiry: f.warranty_expiry || autoExpiry || null,
        amc_details: f.amc_details || null,
        useful_life_years: f.useful_life_years === '' ? null : num(f.useful_life_years),
        salvage_value: num(f.salvage_value) * (group ? rows : 1),
        status: f.status, handler_staff_id: f.handler_staff_id ? String(f.handler_staff_id) : null,
        handler_name: staffRow ? staffRow.full_name : null,
        used_for: f.used_for || null, notes: f.notes || null, attachments: f.attachments || [],
        disposal_date: f.status === 'Disposed' ? (f.disposal_date || null) : null,
        disposal_value: f.status === 'Disposed' && f.disposal_value !== '' ? num(f.disposal_value) : null,
        disposal_reason: f.status === 'Disposed' ? (f.disposal_reason || null) : null,
      };
      let savedIds = [];
      if (editing) {
        const { error } = await supabase.from('assets').update({ ...core, serial_no: f.serial_no || null, updated_by: me }).eq('id', editing.id);
        if (error) throw error;
        savedIds = [editing.id];
      } else {
        const serialLines = String(f.serials || '').split('\n').map(s => s.trim()).filter(Boolean);
        const batch = Array.from({ length: group ? 1 : rows }, (_, i) => ({
          ...core, serial_no: (rows > 1 ? serialLines[i] : (f.serial_no || serialLines[0])) || null, created_by: me,
        }));
        const { data, error } = await supabase.from('assets').insert(batch).select('id, asset_tag');
        if (error) throw error;
        savedIds = (data || []).map(r => r.id);
        if (core.handler_staff_id && savedIds.length) {
          await supabase.from('asset_handovers').insert(savedIds.map(id => ({
            asset_id: id, issued_to_staff_id: core.handler_staff_id, issued_to_name: core.handler_name,
            location_id: core.location_id, department: core.department, issued_on: core.purchase_date || todayStr(),
            purpose: core.used_for || 'Initial assignment', created_by: me,
          })));
        }
        toast.success(rows > 1 && !group ? `${rows} assets created (${(data || []).map(r => r.asset_tag).join(', ')})` : `Asset created (${data?.[0]?.asset_tag || ''})`);
      }
      if (sendAcc && !(editing && editing.accounts_entry_id)) {
        try {
          const id = await sendToAccounts({
            date: core.purchase_date, amount: total, category: 'Assets & Equipment', party: core.shop_name,
            reference: core.invoice_no, company: accCompany, payMode: accMode,
            description: `Asset purchase: ${core.name}${rows > 1 ? ' × ' + rows : ''}${f.department ? ' (' + f.department + ')' : ''}`,
          });
          await supabase.from('assets').update({ accounts_entry_id: String(id) }).in('id', savedIds);
          toast.success('Sent to Accounts for approval');
        } catch (e) { toast.error('Saved, but could not send to Accounts: ' + e.message); }
      }
      if (editing) toast.success('Asset updated');
      onSaved();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  }

  const staffOpts = [{ value: '', label: '— none —' }, ...ctx.staff.map(s => ({ value: String(s.id), label: s.full_name }))];
  const vendorOpts = [{ value: '', label: '— none / other shop —' }, ...ctx.vendors.filter(v => v.is_active !== false).map(v => ({ value: v.id, label: v.name }))];
  return (
    <Modal title={editing ? `Edit asset ${editing.asset_tag || ''}` : 'Add asset'} onClose={onClose} width={880}>
      <SH mt={0}>Basic details</SH>
      <div className="form-grid">
        <FG label="Asset name *" full><input value={f.name} onChange={e => sf('name', e.target.value)} placeholder="e.g. Dell Latitude laptop, Welding machine, Projector" /></FG>
        <FG label="Category"><select value={f.category} onChange={e => sf('category', e.target.value)}><option value="">Select</option>{ASSET_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></FG>
        <FG label="Make / model"><input value={f.make_model} onChange={e => sf('make_model', e.target.value)} /></FG>
        <FG label="Department *"><select value={f.department} onChange={e => sf('department', e.target.value)}><option value="">Select</option>{DEPARTMENTS.map(d => <option key={d}>{d}</option>)}</select></FG>
        <FG label="Location *">
          <select value={f.location_id} onChange={e => sf('location_id', e.target.value)}>
            <option value="">Select</option>
            {(editing && !editableLocs.find(l => l.id === editing.location_id) ? ctx.locations.filter(l => l.id === editing.location_id) : []).concat(editableLocs).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </FG>
        <FG label="Exact place (lab, store, site name…)"><input value={f.specific_place} onChange={e => sf('specific_place', e.target.value)} /></FG>
        <FG label="Tracking">
          <select value={f.tracking} onChange={e => sf('tracking', e.target.value)} disabled={!!editing}>
            <option value="individual">Individual – each unit has its own tag (laptop, machine…)</option>
            <option value="group">Group – one row with a quantity (chairs, tools…)</option>
          </select>
        </FG>
        {group
          ? <FG label="Quantity"><input type="number" min="1" value={f.quantity} onChange={e => sf('quantity', e.target.value)} /></FG>
          : multi
            ? <FG label="Number of units to create"><input type="number" min="1" max="200" value={f.units} onChange={e => sf('units', e.target.value)} /></FG>
            : <FG label="Serial number"><input value={f.serial_no || ''} onChange={e => sf('serial_no', e.target.value)} /></FG>}
        {multi && rows === 1 && <FG label="Serial number"><input value={f.serial_no || ''} onChange={e => sf('serial_no', e.target.value)} /></FG>}
        {multi && rows > 1 && <FG label={`Serial numbers (one per line, optional – ${rows} units)`} full><textarea rows={3} value={f.serials} onChange={e => sf('serials', e.target.value)} /></FG>}
      </div>

      <SH>Purchase</SH>
      <div className="form-grid">
        <FG label="Purchase date"><DateInput value={f.purchase_date} onChange={e => sf('purchase_date', e.target.value)} /></FG>
        <FG label={group ? 'Price per unit (₹)' : 'Price per unit (₹)'}><input type="number" min="0" value={f.unit_price} onChange={e => sf('unit_price', e.target.value)} /></FG>
        <FG label={multi && rows > 1 ? `GST amount – total for all ${rows} (₹)` : 'GST amount (₹)'}><input type="number" min="0" value={f.gst_amount} onChange={e => sf('gst_amount', e.target.value)} /></FG>
        <FG label="Total purchase amount"><div style={{ padding: '8px 0', fontWeight: 700, fontSize: 15 }}>{inr(total)}{rows > 1 ? <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text3)' }}> ({rows} × {inr(f.unit_price)})</span> : null}</div></FG>
        <FG label="Vendor / shop"><SearchableSelect options={vendorOpts} value={f.vendor_id} onChange={v => sf('vendor_id', v || '')} placeholder="Select vendor" searchPlaceholder="Search vendor..." /></FG>
        <FG label="Shop name (if not in list)"><input value={f.shop_name || ''} onChange={e => sf('shop_name', e.target.value)} /></FG>
        <FG label="Invoice / bill no."><input value={f.invoice_no || ''} onChange={e => sf('invoice_no', e.target.value)} /></FG>
        <FG label="Payment mode"><select value={f.payment_mode || ''} onChange={e => sf('payment_mode', e.target.value)}><option value="">Select</option>{ctx.payModes.map(m => <option key={m}>{m}</option>)}</select></FG>
      </div>

      <SH>Warranty & life</SH>
      <div className="form-grid">
        <FG label="Warranty (years)"><input type="number" min="0" step="0.5" value={f.warranty_years} onChange={e => sf('warranty_years', e.target.value)} /></FG>
        <FG label={`Warranty expiry ${autoExpiry && !f.warranty_expiry ? '(auto: ' + fmtDate(autoExpiry) + ')' : ''}`}><DateInput value={f.warranty_expiry} onChange={e => sf('warranty_expiry', e.target.value)} /></FG>
        <FG label="AMC details"><input value={f.amc_details || ''} onChange={e => sf('amc_details', e.target.value)} placeholder="Vendor, validity…" /></FG>
        <FG label="Useful life (years) – for book value"><input type="number" min="0" step="0.5" value={f.useful_life_years} onChange={e => sf('useful_life_years', e.target.value)} /></FG>
        <FG label="Scrap value per unit (₹)"><input type="number" min="0" value={f.salvage_value} onChange={e => sf('salvage_value', e.target.value)} /></FG>
      </div>

      <SH>Use & condition</SH>
      <div className="form-grid">
        <FG label="Who is handling it"><SearchableSelect options={staffOpts} value={f.handler_staff_id} onChange={v => sf('handler_staff_id', v || '')} placeholder="Select staff" searchPlaceholder="Search staff..." /></FG>
        <FG label="Where / what is it used for"><input value={f.used_for || ''} onChange={e => sf('used_for', e.target.value)} placeholder="e.g. Mechanical lab, Kakkanad site" /></FG>
        <FG label="Status"><select value={f.status} onChange={e => sf('status', e.target.value)}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></FG>
        <FG label="Notes" full><textarea rows={2} value={f.notes || ''} onChange={e => sf('notes', e.target.value)} /></FG>
        {f.status === 'Disposed' && <>
          <FG label="Disposal date"><DateInput value={f.disposal_date} onChange={e => sf('disposal_date', e.target.value)} /></FG>
          <FG label="Sale / scrap value (₹)"><input type="number" min="0" value={f.disposal_value} onChange={e => sf('disposal_value', e.target.value)} /></FG>
          <FG label="Reason" full><input value={f.disposal_reason || ''} onChange={e => sf('disposal_reason', e.target.value)} /></FG>
        </>}
      </div>

      <SH>Files</SH>
      <Attachments value={f.attachments} onChange={v => sf('attachments', v)} folder="assets" editable />
      <AccountsBlock enabled={sendAcc} onToggle={setSendAcc} company={accCompany} onCompany={setAccCompany} payMode={accMode} onPayMode={setAccMode}
        payModes={ctx.payModes} already={!!(editing && editing.accounts_entry_id)} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : (editing ? 'Save changes' : (multi && rows > 1 ? `Create ${rows} assets` : 'Save asset'))}</button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ASSET VIEW  (details · handover · maintenance · files)
// ═══════════════════════════════════════════════════════════════════
function AssetView({ ctx, assetId, onClose, onEdit }) {
  const a = ctx.assets.find(x => x.id === assetId);
  const [tab, setTab] = useState('details');
  const [hand, setHand] = useState([]);
  const [maint, setMaint] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hf, setHf] = useState({ staff: '', name: '', location_id: '', department: '', issued_on: todayStr(), purpose: '', notes: '' });
  const [mf, setMf] = useState({ maint_date: todayStr(), maint_type: 'Repair', issue: '', work_done: '', vendor_id: '', shop_name: '', cost: '', next_due: '', set_status: '', attachments: [] });
  const [mAcc, setMAcc] = useState(false);
  const [mCompany, setMCompany] = useState('ARMSTECH');
  const [mMode, setMMode] = useState('Cash');
  const [showHF, setShowHF] = useState(false);
  const [showMF, setShowMF] = useState(false);

  async function loadDetail() {
    if (!assetId) return;
    const [h, m] = await Promise.all([
      supabase.from('asset_handovers').select('*').eq('asset_id', assetId).order('issued_on', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('asset_maintenance').select('*').eq('asset_id', assetId).order('maint_date', { ascending: false }).order('created_at', { ascending: false }),
    ]);
    setHand(h.data || []); setMaint(m.data || []);
  }
  useEffect(() => { loadDetail(); }, [assetId]); // eslint-disable-line
  useLiveRefresh(['asset_handovers', 'asset_maintenance'], () => loadDetail(), !!assetId);

  if (!a) return null;
  const canEditThis = ctx.canEditLoc(a.location_id);
  const mTotal = maint.reduce((t, r) => t + num(r.cost), 0);
  const ratio = num(a.purchase_amount) > 0 ? mTotal / num(a.purchase_amount) : 0;
  const bv = bookValue(a);
  const wd = daysTo(a.warranty_expiry);
  const openHand = hand.find(h => !h.returned_on);

  async function issue() {
    if (!hf.staff && !hf.name.trim()) return toast.error('Choose a staff member or type who it is issued to');
    const loc = hf.location_id || a.location_id;
    if (!ctx.canEditLoc(loc)) return toast.error('You can only issue assets within your own location');
    setBusy(true);
    try {
      const me = await whoAmI();
      const st = ctx.staff.find(s => String(s.id) === String(hf.staff));
      const who = st ? st.full_name : hf.name.trim();
      await supabase.from('asset_handovers').update({ returned_on: hf.issued_on }).eq('asset_id', a.id).is('returned_on', null);
      const { error } = await supabase.from('asset_handovers').insert({
        asset_id: a.id, issued_to_staff_id: st ? String(st.id) : null, issued_to_name: who, location_id: loc,
        department: hf.department || a.department, issued_on: hf.issued_on, purpose: hf.purpose || null, notes: hf.notes || null, created_by: me,
      });
      if (error) throw error;
      const { error: e2 } = await supabase.from('assets').update({
        handler_staff_id: st ? String(st.id) : null, handler_name: who, location_id: loc, department: hf.department || a.department, updated_by: me,
      }).eq('id', a.id);
      if (e2) throw e2;
      toast.success('Handover recorded');
      setShowHF(false); setHf({ staff: '', name: '', location_id: '', department: '', issued_on: todayStr(), purpose: '', notes: '' });
      await loadDetail(); ctx.reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  }
  async function returnToStore() {
    if (!window.confirm('Mark this asset as returned (no one holding it)?')) return;
    setBusy(true);
    try {
      await supabase.from('asset_handovers').update({ returned_on: todayStr() }).eq('asset_id', a.id).is('returned_on', null);
      await supabase.from('assets').update({ handler_staff_id: null, handler_name: null }).eq('id', a.id);
      toast.success('Marked as returned'); await loadDetail(); ctx.reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  }
  async function addMaint() {
    if (!mf.issue.trim() && !mf.work_done.trim()) return toast.error('Describe the issue or the work done');
    setBusy(true);
    try {
      const me = await whoAmI();
      const vendor = ctx.vendors.find(v => v.id === mf.vendor_id);
      const row = {
        asset_id: a.id, maint_date: mf.maint_date || todayStr(), maint_type: mf.maint_type, issue: mf.issue || null, work_done: mf.work_done || null,
        vendor_id: mf.vendor_id || null, shop_name: mf.shop_name || (vendor ? vendor.name : null), cost: num(mf.cost),
        next_due: mf.next_due || null, attachments: mf.attachments || [], created_by: me,
      };
      const { data, error } = await supabase.from('asset_maintenance').insert(row).select('id').single();
      if (error) throw error;
      if (mf.set_status) await supabase.from('assets').update({ status: mf.set_status, updated_by: me }).eq('id', a.id);
      if (mAcc && num(mf.cost) > 0) {
        try {
          const id = await sendToAccounts({
            date: row.maint_date, amount: row.cost, category: 'Asset Repair & Maintenance', party: row.shop_name, reference: a.asset_tag,
            company: mCompany, payMode: mMode, description: `${mf.maint_type} – ${a.name} (${a.asset_tag || ''})${mf.issue ? ': ' + mf.issue : ''}`,
          });
          await supabase.from('asset_maintenance').update({ accounts_entry_id: String(id) }).eq('id', data.id);
        } catch (e) { toast.error('Saved, but could not send to Accounts: ' + e.message); }
      }
      toast.success('Maintenance recorded');
      setShowMF(false); setMAcc(false);
      setMf({ maint_date: todayStr(), maint_type: 'Repair', issue: '', work_done: '', vendor_id: '', shop_name: '', cost: '', next_due: '', set_status: '', attachments: [] });
      await loadDetail(); ctx.reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  }
  async function delMaint(r) {
    if (!window.confirm('Delete this maintenance entry?')) return;
    const { error } = await supabase.from('asset_maintenance').delete().eq('id', r.id);
    if (error) return toast.error(error.message);
    await removeFiles(r.attachments); await loadDetail(); ctx.reload();
  }

  const staffOpts = [{ value: '', label: '— type a name below —' }, ...ctx.staff.map(s => ({ value: String(s.id), label: s.full_name }))];
  const vendorOpts = [{ value: '', label: '— none / other shop —' }, ...ctx.vendors.filter(v => v.is_active !== false).map(v => ({ value: v.id, label: v.name }))];
  const TabBtn = ({ k, label }) => <div className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{label}</div>;

  return (
    <Modal onClose={onClose} width={900} title={<span>🧰 {a.name} <Chip>{a.asset_tag || '—'}</Chip> <StatusBadge status={a.status} /></span>}>
      <div className="tabs" style={{ position: 'static', marginBottom: 12 }}>
        <TabBtn k="details" label="Details" /><TabBtn k="handover" label={`Handover (${hand.length})`} />
        <TabBtn k="maint" label={`Maintenance (${maint.length})`} /><TabBtn k="files" label={`Files (${(a.attachments || []).length})`} />
      </div>

      {tab === 'details' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 24 }}>
          <div>
            <KV label="Category">{a.category || '—'}</KV>
            <KV label="Make / model">{a.make_model || '—'}</KV>
            <KV label="Serial no.">{a.serial_no || '—'}</KV>
            <KV label="Department">{a.department || '—'}</KV>
            <KV label="Location">{ctx.locName(a.location_id)}{a.specific_place ? ' · ' + a.specific_place : ''}</KV>
            <KV label="Tracking">{a.tracking === 'group' ? `Group × ${a.quantity}` : 'Individual'}</KV>
            <KV label="Handled by">{a.handler_name || '—'}</KV>
            <KV label="Used for">{a.used_for || '—'}</KV>
            <KV label="Notes">{a.notes || '—'}</KV>
          </div>
          <div>
            <KV label="Purchased">{fmtDate(a.purchase_date)}</KV>
            <KV label="Amount">{inr(a.purchase_amount)}{num(a.gst_amount) ? <span style={{ color: 'var(--text3)' }}> (GST {inr(a.gst_amount)})</span> : null}</KV>
            <KV label="Shop">{a.shop_name || ctx.vendorName(a.vendor_id) || '—'}</KV>
            <KV label="Invoice no.">{a.invoice_no || '—'}</KV>
            <KV label="Warranty">{a.warranty_years ? a.warranty_years + ' yr' : '—'}{a.warranty_expiry ? <> · till {fmtDate(a.warranty_expiry)} {wd !== null && <Chip bg={wd < 0 ? '#fee2e2' : wd <= EXPIRY_SOON ? '#fef3c7' : '#dcfce7'} color={wd < 0 ? '#991b1b' : wd <= EXPIRY_SOON ? '#92400e' : '#166534'}>{wd < 0 ? 'expired' : wd + ' days left'}</Chip>}</> : null}</KV>
            <KV label="AMC">{a.amc_details || '—'}</KV>
            <KV label="Book value">{bv === null ? <span style={{ color: 'var(--text3)' }}>set useful life to see</span> : inr(bv)}</KV>
            <KV label="Maintenance cost">{inr(mTotal)} {ratio >= REPLACE_RATIO && <Chip bg="#fee2e2" color="#991b1b">⚠ {Math.round(ratio * 100)}% of price – consider replacing</Chip>}</KV>
            <KV label="Accounts">{a.accounts_entry_id ? '✓ Sent to Accounts' : '—'}</KV>
            {a.status === 'Disposed' && <KV label="Disposed">{fmtDate(a.disposal_date)} · {a.disposal_value != null ? inr(a.disposal_value) : '—'} · {a.disposal_reason || ''}</KV>}
          </div>
        </div>
      )}

      {tab === 'handover' && (
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ fontSize: 13 }}>Currently with: <b>{openHand ? openHand.issued_to_name : (a.handler_name || 'nobody (in store)')}</b></div>
            {canEditThis && <button className="btn btn-primary btn-sm" onClick={() => setShowHF(v => !v)}>{showHF ? 'Cancel' : '➡ Issue / transfer'}</button>}
            {canEditThis && (openHand || a.handler_name) && <button className="btn btn-secondary btn-sm" onClick={returnToStore} disabled={busy}>↩ Mark returned</button>}
          </div>
          {showHF && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--surface)' }}>
              <div className="form-grid">
                <FG label="Staff"><SearchableSelect options={staffOpts} value={hf.staff} onChange={v => setHf(p => ({ ...p, staff: v || '' }))} placeholder="Select staff" searchPlaceholder="Search staff..." /></FG>
                <FG label="…or department / project / name"><input value={hf.name} onChange={e => setHf(p => ({ ...p, name: e.target.value }))} /></FG>
                <FG label="Location"><select value={hf.location_id} onChange={e => setHf(p => ({ ...p, location_id: e.target.value }))}><option value="">Same ({ctx.locName(a.location_id)})</option>{ctx.locations.filter(l => l.is_active !== false).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></FG>
                <FG label="Department"><select value={hf.department} onChange={e => setHf(p => ({ ...p, department: e.target.value }))}><option value="">Same ({a.department || '—'})</option>{DEPARTMENTS.map(d => <option key={d}>{d}</option>)}</select></FG>
                <FG label="Date"><DateInput value={hf.issued_on} onChange={e => setHf(p => ({ ...p, issued_on: e.target.value }))} /></FG>
                <FG label="Purpose"><input value={hf.purpose} onChange={e => setHf(p => ({ ...p, purpose: e.target.value }))} /></FG>
              </div>
              <div style={{ textAlign: 'right', marginTop: 8 }}><button className="btn btn-primary btn-sm" onClick={issue} disabled={busy}>Save handover</button></div>
            </div>
          )}
          {hand.length === 0 ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No handover history yet.</div> : (
            <table style={{ fontSize: 12 }}>
              <thead><tr><th>Issued to</th><th>Location</th><th>Dept</th><th>From</th><th>Returned</th><th>Purpose</th></tr></thead>
              <tbody>{hand.map(h => (
                <tr key={h.id}><td style={{ fontWeight: 600 }}>{h.issued_to_name || '—'}</td><td>{ctx.locName(h.location_id)}</td><td>{h.department || '—'}</td><td>{fmtDate(h.issued_on)}</td><td>{h.returned_on ? fmtDate(h.returned_on) : <Chip bg="#dcfce7" color="#166534">holding</Chip>}</td><td>{h.purpose || '—'}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'maint' && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ fontSize: 13 }}>Lifetime cost: <b>{inr(mTotal)}</b>{ratio >= REPLACE_RATIO && <Chip bg="#fee2e2" color="#991b1b"> ⚠ {Math.round(ratio * 100)}% of purchase price</Chip>}</div>
            {canEditThis && <button className="btn btn-primary btn-sm" onClick={() => setShowMF(v => !v)}>{showMF ? 'Cancel' : '+ Add maintenance'}</button>}
          </div>
          {showMF && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--surface)' }}>
              <div className="form-grid">
                <FG label="Date"><DateInput value={mf.maint_date} onChange={e => setMf(p => ({ ...p, maint_date: e.target.value }))} /></FG>
                <FG label="Type"><select value={mf.maint_type} onChange={e => setMf(p => ({ ...p, maint_type: e.target.value }))}>{MAINT_TYPES.map(t => <option key={t}>{t}</option>)}</select></FG>
                <FG label="Issue / problem" full><input value={mf.issue} onChange={e => setMf(p => ({ ...p, issue: e.target.value }))} /></FG>
                <FG label="Work done" full><input value={mf.work_done} onChange={e => setMf(p => ({ ...p, work_done: e.target.value }))} /></FG>
                <FG label="Vendor / service centre"><SearchableSelect options={vendorOpts} value={mf.vendor_id} onChange={v => setMf(p => ({ ...p, vendor_id: v || '' }))} placeholder="Select" searchPlaceholder="Search vendor..." /></FG>
                <FG label="…or shop name"><input value={mf.shop_name} onChange={e => setMf(p => ({ ...p, shop_name: e.target.value }))} /></FG>
                <FG label="Cost (₹)"><input type="number" min="0" value={mf.cost} onChange={e => setMf(p => ({ ...p, cost: e.target.value }))} /></FG>
                <FG label="Next service due"><DateInput value={mf.next_due} onChange={e => setMf(p => ({ ...p, next_due: e.target.value }))} /></FG>
                <FG label="Update asset status"><select value={mf.set_status} onChange={e => setMf(p => ({ ...p, set_status: e.target.value }))}><option value="">No change</option><option>Working</option><option>Under repair</option><option>Damaged</option></select></FG>
              </div>
              <div style={{ marginTop: 8 }}><Attachments value={mf.attachments} onChange={v => setMf(p => ({ ...p, attachments: v }))} folder="maintenance" editable /></div>
              <AccountsBlock enabled={mAcc} onToggle={setMAcc} company={mCompany} onCompany={setMCompany} payMode={mMode} onPayMode={setMMode} payModes={ctx.payModes} already={false} />
              <div style={{ textAlign: 'right', marginTop: 8 }}><button className="btn btn-primary btn-sm" onClick={addMaint} disabled={busy}>Save</button></div>
            </div>
          )}
          {maint.length === 0 ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No maintenance recorded.</div> : (
            <table style={{ fontSize: 12 }}>
              <thead><tr><th>Date</th><th>Type</th><th>Issue / work done</th><th>Shop</th><th style={{ textAlign: 'right' }}>Cost</th><th>Next due</th><th /></tr></thead>
              <tbody>{maint.map(r => (
                <tr key={r.id}>
                  <td>{fmtDate(r.maint_date)}</td><td>{r.maint_type}</td>
                  <td style={{ maxWidth: 300 }}>{r.issue || ''}{r.issue && r.work_done ? ' → ' : ''}{r.work_done || ''}{(r.attachments || []).length > 0 && <span> 📎{r.attachments.map((x, i) => <a key={i} href="#o" style={{ marginLeft: 4, color: 'var(--accent)' }} onClick={e => { e.preventDefault(); openAttachment(x); }}>{i + 1}</a>)}</span>}</td>
                  <td>{r.shop_name || ctx.vendorName(r.vendor_id) || '—'}</td><td style={{ textAlign: 'right' }}>{inr(r.cost)}</td><td>{fmtDate(r.next_due)}</td>
                  <td>{canEditThis && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => delMaint(r)}><Trash2 size={12} /></button>}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'files' && <Attachments value={a.attachments} onChange={() => {}} folder="assets" editable={false} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        {canEditThis && <button className="btn btn-secondary" onClick={() => onEdit(a)}>Edit asset</button>}
        <button className="btn btn-primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ASSETS TAB (list)
// ═══════════════════════════════════════════════════════════════════
function AssetsTab({ ctx, preset, clearPreset }) {
  const [q, setQ] = useState('');
  const [fDept, setFDept] = useState('');
  const [fLoc, setFLoc] = useState('');
  const [fCat, setFCat] = useState('');
  const [fStatus, setFStatus] = useState('');          // '' = in use (not disposed / lost), '__all' = everything
  const [fWarranty, setFWarranty] = useState(false);
  const [fService, setFService] = useState(false);
  const [fHeavy, setFHeavy] = useState(false);
  const [sort, setSort] = useState('new');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewId, setViewId] = useState(null);

  useEffect(() => {
    if (!preset) return;
    setQ(''); setFDept(preset.department || ''); setFLoc(preset.location || ''); setFCat(''); setFStatus(preset.status || '');
    setFWarranty(!!preset.warranty); setFService(!!preset.service); setFHeavy(!!preset.heavy); setPage(1);
    if (clearPreset) clearPreset();
  }, [preset]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [q, fDept, fLoc, fCat, fStatus, fWarranty, fService, fHeavy, sort]);

  const msum = useMemo(() => summarizeMaint(ctx.maint), [ctx.maint]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = ctx.assets.filter(a => {
      if (fStatus === '') { if (a.status === 'Disposed' || a.status === 'Lost') return false; }
      else if (fStatus === '__bad') { if (a.status !== 'Damaged' && a.status !== 'Under repair') return false; }
      else if (fStatus !== '__all' && a.status !== fStatus) return false;
      if (fDept && a.department !== fDept) return false;
      if (fLoc && a.location_id !== fLoc) return false;
      if (fCat && a.category !== fCat) return false;
      if (fWarranty) { const d = daysTo(a.warranty_expiry); if (d === null || d > EXPIRY_SOON) return false; }
      if (fService) { const d = daysTo((msum[a.id] || {}).nextDue); if (d === null || d > EXPIRY_SOON) return false; }
      if (fHeavy) { const m = msum[a.id]; if (!m || !(num(a.purchase_amount) > 0) || m.cost / num(a.purchase_amount) < REPLACE_RATIO) return false; }
      if (s) {
        const hay = [a.name, a.asset_tag, a.serial_no, a.make_model, a.handler_name, a.shop_name, a.used_for, a.specific_place, a.invoice_no].join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
    const by = {
      new: (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')),
      name: (a, b) => String(a.name).localeCompare(String(b.name)),
      date: (a, b) => String(b.purchase_date || '').localeCompare(String(a.purchase_date || '')),
      amount: (a, b) => num(b.purchase_amount) - num(a.purchase_amount),
      warranty: (a, b) => String(a.warranty_expiry || '9999').localeCompare(String(b.warranty_expiry || '9999')),
    }[sort];
    return [...list].sort(by);
  }, [ctx.assets, msum, q, fDept, fLoc, fCat, fStatus, fWarranty, fService, fHeavy, sort]);

  const units = filtered.reduce((t, a) => t + (num(a.quantity) || 1), 0);
  const value = filtered.reduce((t, a) => t + num(a.purchase_amount), 0);
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function exportXlsx() {
    if (!filtered.length) return;
    const rows = filtered.map((a, i) => {
      const m = msum[a.id] || {}; const bv = bookValue(a);
      return {
        '#': i + 1, 'Asset Tag': a.asset_tag || '', 'Name': a.name, 'Category': a.category || '', 'Make / Model': a.make_model || '', 'Serial No': a.serial_no || '',
        'Department': a.department || '', 'Location': ctx.locName(a.location_id), 'Place': a.specific_place || '', 'Tracking': a.tracking, 'Qty': a.quantity,
        'Purchase Date': a.purchase_date || '', 'Purchase Amount': num(a.purchase_amount), 'GST': num(a.gst_amount), 'Shop': a.shop_name || ctx.vendorName(a.vendor_id) || '',
        'Invoice No': a.invoice_no || '', 'Warranty Years': a.warranty_years ?? '', 'Warranty Expiry': a.warranty_expiry || '', 'AMC': a.amc_details || '',
        'Handled By': a.handler_name || '', 'Used For': a.used_for || '', 'Status': a.status, 'Maintenance Count': m.count || 0, 'Maintenance Cost': num(m.cost),
        'Next Service Due': m.nextDue || '', 'Book Value': bv === null ? '' : Math.round(bv), 'Notes': a.notes || '',
      };
    });
    saveStyledXlsx('Assets_' + todayStr() + '.xlsx', [{ name: 'Assets', title: 'Asset Register', rows }]);
  }
  async function del(a) {
    if (!window.confirm(`Delete ${a.asset_tag || ''} "${a.name}" and its handover / maintenance history?\n\n(To retire an asset, set its status to Disposed instead.)`)) return;
    const { error } = await supabase.from('assets').delete().eq('id', a.id);
    if (error) return toast.error(error.message);
    await removeFiles(a.attachments); toast.success('Deleted'); ctx.reload();
  }

  const filtersOn = q || fDept || fLoc || fCat || fStatus || fWarranty || fService || fHeavy;
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <div className="search-bar" style={{ width: 240 }}><Search size={14} /><input placeholder="Search name, tag, serial, shop…" value={q} onChange={e => setQ(e.target.value)} /></div>
        <select style={{ width: 130 }} value={fDept} onChange={e => setFDept(e.target.value)}><option value="">All depts</option>{DEPARTMENTS.map(d => <option key={d}>{d}</option>)}</select>
        <select style={{ width: 150 }} value={fLoc} onChange={e => setFLoc(e.target.value)}><option value="">All locations</option>{ctx.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        <select style={{ width: 160 }} value={fCat} onChange={e => setFCat(e.target.value)}><option value="">All categories</option>{ASSET_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
        <select style={{ width: 140 }} value={fStatus} onChange={e => setFStatus(e.target.value)}><option value="">In use</option><option value="__bad">Damaged / under repair</option><option value="__all">Everything</option>{STATUSES.map(s => <option key={s}>{s}</option>)}</select>
        <select style={{ width: 150 }} value={sort} onChange={e => setSort(e.target.value)}>
          <option value="new">Newest added</option><option value="name">Name A–Z</option><option value="date">Purchase date</option><option value="amount">Highest amount</option><option value="warranty">Warranty ending soon</option>
        </select>
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={fWarranty} onChange={e => setFWarranty(e.target.checked)} />Warranty ≤{EXPIRY_SOON}d</label>
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={fService} onChange={e => setFService(e.target.checked)} />Service due</label>
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={fHeavy} onChange={e => setFHeavy(e.target.checked)} />Repair-heavy</label>
        {filtersOn && <button className="btn btn-ghost btn-sm" onClick={() => { setQ(''); setFDept(''); setFLoc(''); setFCat(''); setFStatus(''); setFWarranty(false); setFService(false); setFHeavy(false); }}>✕ Clear</button>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={exportXlsx} disabled={!filtered.length}><Download size={13} /> Excel</button>
          {ctx.canAddAny && <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowForm(true); }}><Plus size={13} /> Add asset</button>}
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{filtered.length} rows · {units} units · purchase value {inr(value)}</div>

      <div className="table-wrap">
        <table style={{ minWidth: 1100 }}>
          <thead><tr>
            <th>Asset</th><th>Dept / Location</th><th>Qty</th><th>Purchase</th><th>Warranty</th><th>Handled by / used for</th><th style={{ textAlign: 'right' }}>Maint. cost</th><th>Status</th><th />
          </tr></thead>
          <tbody>
            {pageRows.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>{ctx.assets.length === 0 ? 'No assets yet – click “Add asset”.' : 'No assets match these filters.'}</td></tr>}
            {pageRows.map(a => {
              const m = msum[a.id] || {}; const wd = daysTo(a.warranty_expiry); const sd = daysTo(m.nextDue);
              return (
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setViewId(a.id)}>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontWeight: 600, color: 'var(--accent)' }}>{a.name}</div>
                    <div style={{ marginTop: 2 }}><Chip>{a.asset_tag || '—'}</Chip>{a.serial_no ? <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6, fontFamily: 'monospace' }}>S/N {a.serial_no}</span> : null}</div>
                    {a.make_model ? <div style={{ fontSize: 11, color: 'var(--text3)' }}>{a.make_model}</div> : null}
                  </td>
                  <td style={{ verticalAlign: 'top', fontSize: 12 }}>{a.department || '—'}<div style={{ color: 'var(--text3)', fontSize: 11 }}>{ctx.locName(a.location_id)}{a.specific_place ? ' · ' + a.specific_place : ''}</div></td>
                  <td style={{ verticalAlign: 'top' }}>{a.tracking === 'group' ? a.quantity : 1}</td>
                  <td style={{ verticalAlign: 'top', fontSize: 12 }}>{fmtDate(a.purchase_date)}<div style={{ fontWeight: 600 }}>{inr(a.purchase_amount)}</div><div style={{ color: 'var(--text3)', fontSize: 11 }}>{a.shop_name || ctx.vendorName(a.vendor_id) || ''}</div></td>
                  <td style={{ verticalAlign: 'top', fontSize: 12 }}>
                    {a.warranty_expiry ? <>{fmtDate(a.warranty_expiry)}<div>{wd !== null && <Chip bg={wd < 0 ? '#fee2e2' : wd <= EXPIRY_SOON ? '#fef3c7' : '#dcfce7'} color={wd < 0 ? '#991b1b' : wd <= EXPIRY_SOON ? '#92400e' : '#166534'}>{wd < 0 ? 'expired' : wd + 'd left'}</Chip>}</div></> : '—'}
                    {sd !== null && sd <= EXPIRY_SOON && <div><Chip bg="#dbeafe" color="#1e40af">service {sd < 0 ? 'overdue' : 'in ' + sd + 'd'}</Chip></div>}
                  </td>
                  <td style={{ verticalAlign: 'top', fontSize: 12 }}>{a.handler_name || '—'}<div style={{ color: 'var(--text3)', fontSize: 11 }}>{a.used_for || ''}</div></td>
                  <td style={{ verticalAlign: 'top', textAlign: 'right', fontSize: 12 }}>{m.count ? inr(m.cost) : '—'}{m.count && num(a.purchase_amount) > 0 && m.cost / num(a.purchase_amount) >= REPLACE_RATIO ? <div><Chip bg="#fee2e2" color="#991b1b">⚠ replace?</Chip></div> : null}</td>
                  <td style={{ verticalAlign: 'top' }}><StatusBadge status={a.status} /></td>
                  <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" title="Open" onClick={() => setViewId(a.id)}><Eye size={13} /></button>
                    {ctx.canEditLoc(a.location_id) && <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => { setEditing(a); setShowForm(true); }}><Edit2 size={13} /></button>}
                    {ctx.canEditLoc(a.location_id) && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete" onClick={() => del(a)}><Trash2 size={13} /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager page={page} setPage={setPage} total={filtered.length} />

      {showForm && <AssetForm ctx={ctx} editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); ctx.reload(); }} />}
      {viewId && <AssetView ctx={ctx} assetId={viewId} onClose={() => setViewId(null)} onEdit={a => { setViewId(null); setEditing(a); setShowForm(true); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  CONSUMABLES
// ═══════════════════════════════════════════════════════════════════
const MOVE_LABEL = { IN: 'Stock in', OUT: 'Issued', TRANSFER_IN: 'Transfer in', TRANSFER_OUT: 'Transfer out', ADJUST_ADD: 'Adjust +', ADJUST_SUB: 'Adjust −' };
const MOVE_COLOR = { IN: ['#dcfce7', '#166534'], OUT: ['#fee2e2', '#991b1b'], TRANSFER_IN: ['#dbeafe', '#1e40af'], TRANSFER_OUT: ['#ede9fe', '#5b21b6'], ADJUST_ADD: ['#fef3c7', '#92400e'], ADJUST_SUB: ['#fef3c7', '#92400e'] };
const stockKey = (item, loc) => item + '|' + loc;
const newRef = () => (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 3 | 8)).toString(16); });

function ItemForm({ ctx, editing, onClose, onSaved }) {
  const [f, setF] = useState(() => editing ? { name: editing.name, category: editing.category || '', unit: editing.unit || 'pcs', min_stock: editing.min_stock ?? '', department: editing.department || '', notes: editing.notes || '', is_active: editing.is_active !== false } : { name: '', category: '', unit: 'pcs', min_stock: '', department: '', notes: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const sf = (k, v) => setF(p => ({ ...p, [k]: v }));
  async function save() {
    if (!f.name.trim()) return toast.error('Item name is required');
    setSaving(true);
    const body = { name: f.name.trim(), category: f.category || null, unit: f.unit || 'pcs', min_stock: num(f.min_stock), department: f.department || null, notes: f.notes || null, is_active: f.is_active };
    const { error } = editing ? await supabase.from('consumable_items').update(body).eq('id', editing.id) : await supabase.from('consumable_items').insert(body);
    setSaving(false);
    if (error) return toast.error(/duplicate|unique/i.test(error.message) ? 'An item with this name already exists' : error.message);
    toast.success(editing ? 'Item updated' : 'Item added'); onSaved();
  }
  return (
    <Modal title={editing ? 'Edit item' : 'Add consumable item'} onClose={onClose} width={620}>
      <div className="form-grid">
        <FG label="Item name *" full><input value={f.name} onChange={e => sf('name', e.target.value)} placeholder="e.g. A4 paper, Marker pen, Welding rod 3.15mm" /></FG>
        <FG label="Category"><select value={f.category} onChange={e => sf('category', e.target.value)}><option value="">Select</option>{CONS_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></FG>
        <FG label="Unit"><select value={f.unit} onChange={e => sf('unit', e.target.value)}>{UNITS.map(u => <option key={u}>{u}</option>)}</select></FG>
        <FG label="Minimum stock (alert level, per location)"><input type="number" min="0" value={f.min_stock} onChange={e => sf('min_stock', e.target.value)} /></FG>
        <FG label="Department"><select value={f.department} onChange={e => sf('department', e.target.value)}><option value="">Any</option>{DEPARTMENTS.map(d => <option key={d}>{d}</option>)}</select></FG>
        <FG label="Notes" full><input value={f.notes} onChange={e => sf('notes', e.target.value)} /></FG>
        {editing && <FG label="Active"><select value={f.is_active ? 'y' : 'n'} onChange={e => sf('is_active', e.target.value === 'y')}><option value="y">Active</option><option value="n">Inactive (hide)</option></select></FG>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

// Stock in / issue / transfer / adjust in one dialog
function MoveModal({ ctx, mode, presetItem, presetLoc, onClose, onSaved }) {
  const editableLocs = ctx.locations.filter(l => l.is_active !== false && ctx.canEditLoc(l.id));
  const [f, setF] = useState({
    item_id: presetItem || '', location_id: presetLoc || (editableLocs[0] && editableLocs[0].id) || '', to_location_id: '',
    qty: '', rate: '', amount: '', move_date: todayStr(), vendor_id: '', shop_name: '', bill_no: '', expiry_date: '',
    staff: '', name: '', department: '', purpose: '', notes: '', adj: 'ADJUST_ADD', attachments: [],
  });
  const [saving, setSaving] = useState(false);
  const [sendAcc, setSendAcc] = useState(false);
  const [accCompany, setAccCompany] = useState('ARMSTECH');
  const [accMode, setAccMode] = useState('Cash');
  const sf = (k, v) => setF(p => ({ ...p, [k]: v }));
  const item = ctx.items.find(i => i.id === f.item_id);
  const bal = f.item_id && f.location_id ? num((ctx.stockMap[stockKey(f.item_id, f.location_id)] || {}).balance) : null;
  const amount = f.amount !== '' ? num(f.amount) : num(f.qty) * num(f.rate);
  const title = { IN: '📥 Stock in (purchase)', OUT: '📤 Issue stock', TRANSFER: '🔁 Transfer between locations', ADJUST: '🛠 Stock correction' }[mode];

  async function save() {
    if (!f.item_id) return toast.error('Choose the item');
    if (!f.location_id) return toast.error('Choose the location');
    if (!ctx.canEditLoc(f.location_id)) return toast.error('You can only change stock of your own location');
    const qty = num(f.qty);
    if (!(qty > 0)) return toast.error('Enter a quantity greater than zero');
    if (mode === 'TRANSFER') {
      if (!f.to_location_id) return toast.error('Choose the destination location');
      if (f.to_location_id === f.location_id) return toast.error('Source and destination are the same');
    }
    if (mode === 'ADJUST' && !f.notes.trim()) return toast.error('Give a reason for the correction');
    if ((mode === 'OUT' || mode === 'TRANSFER' || (mode === 'ADJUST' && f.adj === 'ADJUST_SUB')) && bal !== null && qty > bal) {
      if (!window.confirm(`Only ${bal} ${item?.unit || ''} in stock here. Continue and let the balance go negative?`)) return;
    }
    if (sendAcc && !(amount > 0)) return toast.error('Enter the rate or amount to send it to Accounts');
    setSaving(true);
    try {
      const me = await whoAmI();
      const st = ctx.staff.find(s => String(s.id) === String(f.staff));
      const vendor = ctx.vendors.find(v => v.id === f.vendor_id);
      const base = { item_id: f.item_id, move_date: f.move_date || todayStr(), qty, notes: f.notes || null, created_by: me };
      let ids = [];
      if (mode === 'IN') {
        const { data, error } = await supabase.from('consumable_moves').insert({
          ...base, location_id: f.location_id, move_type: 'IN', rate: f.rate === '' ? null : num(f.rate), amount: amount || null,
          vendor_id: f.vendor_id || null, shop_name: f.shop_name || (vendor ? vendor.name : null), bill_no: f.bill_no || null,
          expiry_date: f.expiry_date || null, attachments: f.attachments || [],
        }).select('id'); if (error) throw error; ids = (data || []).map(r => r.id);
      } else if (mode === 'OUT') {
        const { error } = await supabase.from('consumable_moves').insert({
          ...base, location_id: f.location_id, move_type: 'OUT', issued_to_staff_id: st ? String(st.id) : null,
          issued_to_name: st ? st.full_name : (f.name.trim() || null), department: f.department || null, purpose: f.purpose || null,
        }); if (error) throw error;
      } else if (mode === 'TRANSFER') {
        const ref = newRef();
        const { error } = await supabase.from('consumable_moves').insert([
          { ...base, location_id: f.location_id, move_type: 'TRANSFER_OUT', transfer_ref: ref, purpose: 'To ' + ctx.locName(f.to_location_id) },
          { ...base, location_id: f.to_location_id, move_type: 'TRANSFER_IN', transfer_ref: ref, purpose: 'From ' + ctx.locName(f.location_id) },
        ]); if (error) throw error;
      } else {
        const { error } = await supabase.from('consumable_moves').insert({ ...base, location_id: f.location_id, move_type: f.adj }); if (error) throw error;
      }
      if (mode === 'IN' && sendAcc) {
        try {
          const id = await sendToAccounts({
            date: base.move_date, amount, category: 'Stationery & Consumables', party: f.shop_name || (vendor ? vendor.name : ''),
            reference: f.bill_no, company: accCompany, payMode: accMode, description: `Consumables: ${item ? item.name : ''} × ${qty}`,
          });
          if (ids.length) await supabase.from('consumable_moves').update({ accounts_entry_id: String(id) }).in('id', ids);
          toast.success('Sent to Accounts for approval');
        } catch (e) { toast.error('Saved, but could not send to Accounts: ' + e.message); }
      }
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  }

  const itemOpts = ctx.items.filter(i => i.is_active !== false).map(i => ({ value: i.id, label: i.name + (i.unit ? ' (' + i.unit + ')' : '') }));
  const staffOpts = [{ value: '', label: '— type a name below —' }, ...ctx.staff.map(s => ({ value: String(s.id), label: s.full_name }))];
  const vendorOpts = [{ value: '', label: '— none / other shop —' }, ...ctx.vendors.filter(v => v.is_active !== false).map(v => ({ value: v.id, label: v.name }))];
  return (
    <Modal title={title} onClose={onClose} width={760}>
      <div className="form-grid">
        <FG label="Item *"><SearchableSelect options={itemOpts} value={f.item_id} onChange={v => sf('item_id', v || '')} placeholder="Select item" searchPlaceholder="Search item..." /></FG>
        <FG label={mode === 'TRANSFER' ? 'From location *' : 'Location *'}>
          <select value={f.location_id} onChange={e => sf('location_id', e.target.value)}><option value="">Select</option>{editableLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        </FG>
        {mode === 'TRANSFER' && <FG label="To location *"><select value={f.to_location_id} onChange={e => sf('to_location_id', e.target.value)}><option value="">Select</option>{ctx.locations.filter(l => l.is_active !== false && l.id !== f.location_id).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></FG>}
        {mode === 'ADJUST' && <FG label="Correction"><select value={f.adj} onChange={e => sf('adj', e.target.value)}><option value="ADJUST_ADD">Add to stock (found / counted more)</option><option value="ADJUST_SUB">Remove from stock (lost / damaged / counted less)</option></select></FG>}
        <FG label={`Quantity *${item ? ' (' + item.unit + ')' : ''}`}><input type="number" min="0" step="any" value={f.qty} onChange={e => sf('qty', e.target.value)} /></FG>
        <FG label="Date"><DateInput value={f.move_date} onChange={e => sf('move_date', e.target.value)} /></FG>
        {bal !== null && <div style={{ gridColumn: '1/-1', fontSize: 12, color: bal <= num(item?.min_stock) ? 'var(--red)' : 'var(--text2)' }}>Current balance here: <b>{bal} {item?.unit}</b>{item && num(item.min_stock) > 0 ? ` · minimum ${item.min_stock}` : ''}</div>}
      </div>

      {mode === 'IN' && <>
        <SH>Purchase details</SH>
        <div className="form-grid">
          <FG label="Rate per unit (₹)"><input type="number" min="0" step="any" value={f.rate} onChange={e => sf('rate', e.target.value)} /></FG>
          <FG label="Total amount (₹)"><input type="number" min="0" step="any" value={f.amount} placeholder={amount ? String(amount) : ''} onChange={e => sf('amount', e.target.value)} /></FG>
          <FG label="Vendor / shop"><SearchableSelect options={vendorOpts} value={f.vendor_id} onChange={v => sf('vendor_id', v || '')} placeholder="Select vendor" searchPlaceholder="Search vendor..." /></FG>
          <FG label="…or shop name"><input value={f.shop_name} onChange={e => sf('shop_name', e.target.value)} /></FG>
          <FG label="Bill no."><input value={f.bill_no} onChange={e => sf('bill_no', e.target.value)} /></FG>
          <FG label="Expiry date (if any)"><DateInput value={f.expiry_date} onChange={e => sf('expiry_date', e.target.value)} /></FG>
        </div>
        <div style={{ marginTop: 8 }}><Attachments value={f.attachments} onChange={v => sf('attachments', v)} folder="consumables" editable /></div>
        <AccountsBlock enabled={sendAcc} onToggle={setSendAcc} company={accCompany} onCompany={setAccCompany} payMode={accMode} onPayMode={setAccMode} payModes={ctx.payModes} already={false} />
      </>}
      {mode === 'OUT' && <>
        <SH>Issued to</SH>
        <div className="form-grid">
          <FG label="Staff"><SearchableSelect options={staffOpts} value={f.staff} onChange={v => sf('staff', v || '')} placeholder="Select staff" searchPlaceholder="Search staff..." /></FG>
          <FG label="…or department / batch / project"><input value={f.name} onChange={e => sf('name', e.target.value)} /></FG>
          <FG label="Department"><select value={f.department} onChange={e => sf('department', e.target.value)}><option value="">Select</option>{DEPARTMENTS.map(d => <option key={d}>{d}</option>)}</select></FG>
          <FG label="Purpose"><input value={f.purpose} onChange={e => sf('purpose', e.target.value)} /></FG>
        </div>
      </>}
      <FG label={mode === 'ADJUST' ? 'Reason *' : 'Notes'} full style={{ marginTop: 10 }}><input value={f.notes} onChange={e => sf('notes', e.target.value)} /></FG>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

function ItemView({ ctx, itemId, onClose, onEdit }) {
  const it = ctx.items.find(i => i.id === itemId);
  if (!it) return null;
  const moves = ctx.moves.filter(m => m.item_id === itemId);
  const ins = moves.filter(m => m.move_type === 'IN');
  const spent = ins.reduce((t, m) => t + num(m.amount), 0);
  const byShop = {};
  ins.forEach(m => { const k = m.shop_name || ctx.vendorName(m.vendor_id) || '—'; const r = byShop[k] || (byShop[k] = { qty: 0, amt: 0, last: null, lastRate: null }); r.qty += num(m.qty); r.amt += num(m.amount); if (!r.last || m.move_date > r.last) { r.last = m.move_date; r.lastRate = m.rate; } });
  return (
    <Modal title={`📦 ${it.name}`} onClose={onClose} width={820}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {it.category && <Chip>{it.category}</Chip>}<Chip bg="#e5e7eb" color="#374151">unit: {it.unit}</Chip>{num(it.min_stock) > 0 && <Chip bg="#fef3c7" color="#92400e">min {it.min_stock}</Chip>}{it.department && <Chip bg="#dbeafe" color="#1e40af">{it.department}</Chip>}
      </div>
      <SH mt={4}>Stock by location</SH>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {ctx.locations.filter(l => l.is_active !== false).map(l => {
          const s = ctx.stockMap[stockKey(it.id, l.id)]; const b = num(s && s.balance);
          return <div key={l.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', minWidth: 130, background: b <= 0 ? '#fef2f2' : b <= num(it.min_stock) ? '#fffbeb' : '#f0fdf4' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l.name}</div><div style={{ fontSize: 20, fontWeight: 700 }}>{b} <span style={{ fontSize: 11, fontWeight: 400 }}>{it.unit}</span></div></div>;
        })}
      </div>
      {Object.keys(byShop).length > 0 && <>
        <SH>Price history by shop <span style={{ fontWeight: 400, textTransform: 'none' }}>(total bought {inr(spent)})</span></SH>
        <table style={{ fontSize: 12 }}><thead><tr><th>Shop</th><th style={{ textAlign: 'right' }}>Qty bought</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Avg rate</th><th>Last bought</th><th style={{ textAlign: 'right' }}>Last rate</th></tr></thead>
          <tbody>{Object.entries(byShop).map(([k, r]) => <tr key={k}><td style={{ fontWeight: 600 }}>{k}</td><td style={{ textAlign: 'right' }}>{r.qty}</td><td style={{ textAlign: 'right' }}>{inr(r.amt)}</td><td style={{ textAlign: 'right' }}>{r.qty ? inr(r.amt / r.qty) : '—'}</td><td>{fmtDate(r.last)}</td><td style={{ textAlign: 'right' }}>{r.lastRate != null ? inr(r.lastRate) : '—'}</td></tr>)}</tbody></table>
      </>}
      <SH>Recent movements</SH>
      {moves.length === 0 ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No movements yet.</div> : (
        <table style={{ fontSize: 12 }}><thead><tr><th>Date</th><th>Type</th><th>Location</th><th style={{ textAlign: 'right' }}>Qty</th><th>Details</th></tr></thead>
          <tbody>{moves.slice(0, 25).map(m => <tr key={m.id}><td>{fmtDate(m.move_date)}</td><td><span style={{ background: MOVE_COLOR[m.move_type][0], color: MOVE_COLOR[m.move_type][1], borderRadius: 8, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{MOVE_LABEL[m.move_type]}</span></td><td>{ctx.locName(m.location_id)}</td><td style={{ textAlign: 'right' }}>{m.qty}</td>
            <td style={{ fontSize: 11 }}>{m.move_type === 'IN' ? [m.shop_name || ctx.vendorName(m.vendor_id), m.rate != null ? '@' + inr(m.rate) : '', m.bill_no ? 'bill ' + m.bill_no : ''].filter(Boolean).join(' · ') : [m.issued_to_name, m.department, m.purpose, m.notes].filter(Boolean).join(' · ')}</td></tr>)}</tbody></table>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        {ctx.isManager && <button className="btn btn-secondary" onClick={() => onEdit(it)}>Edit item</button>}
        <button className="btn btn-primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function ConsumablesTab({ ctx, preset, clearPreset }) {
  const myLoc = ctx.locations.find(l => ctx.myStaffId && (l.incharge_ids || []).includes(String(ctx.myStaffId)));
  const [view, setView] = useState('stock');
  const [q, setQ] = useState('');
  const [fLoc, setFLoc] = useState(myLoc ? myLoc.id : '');
  const [fCat, setFCat] = useState('');
  const [fDept, setFDept] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [dlg, setDlg] = useState(null);             // { mode, item, loc }
  const [itemForm, setItemForm] = useState(null);   // { editing }
  const [viewItem, setViewItem] = useState(null);
  // movements filters
  const [mType, setMType] = useState('');
  const [mFrom, setMFrom] = useState('');
  const [mTo, setMTo] = useState('');
  const [mPage, setMPage] = useState(1);

  useEffect(() => { if (preset) { setView('stock'); setLowOnly(!!preset.low); setFLoc(preset.location || ''); if (clearPreset) clearPreset(); } }, [preset]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [q, fLoc, fCat, fDept, lowOnly]);
  useEffect(() => { setMPage(1); }, [q, fLoc, mType, mFrom, mTo]);

  const activeLocs = ctx.locations.filter(l => l.is_active !== false);
  const lastIn = useMemo(() => { const m = {}; ctx.moves.forEach(r => { if (r.move_type === 'IN' && !m[r.item_id]) m[r.item_id] = r; }); return m; }, [ctx.moves]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return ctx.items.filter(i => i.is_active !== false).map(i => {
      const per = activeLocs.map(l => ({ loc: l, bal: num((ctx.stockMap[stockKey(i.id, l.id)] || {}).balance) }));
      const bal = fLoc ? num((ctx.stockMap[stockKey(i.id, fLoc)] || {}).balance) : per.reduce((t, p) => t + p.bal, 0);
      const min = num(i.min_stock);
      const low = min > 0 ? (fLoc ? bal <= min : per.some(p => p.bal <= min && (p.bal !== 0 || ctx.stockMap[stockKey(i.id, p.loc.id)]))) : false;
      return { item: i, per, bal, low, out: bal <= 0 };
    }).filter(r => {
      if (fCat && r.item.category !== fCat) return false;
      if (fDept && r.item.department !== fDept) return false;
      if (lowOnly && !(r.low || r.out)) return false;
      if (s && !(r.item.name + ' ' + (r.item.category || '')).toLowerCase().includes(s)) return false;
      return true;
    }).sort((a, b) => a.item.name.localeCompare(b.item.name));
  }, [ctx.items, ctx.stockMap, activeLocs.length, q, fLoc, fCat, fDept, lowOnly]); // eslint-disable-line
  const lowRows = rows.filter(r => r.low || (r.out && num(r.item.min_stock) > 0));

  const moves = useMemo(() => {
    const s = q.trim().toLowerCase();
    return ctx.moves.filter(m => {
      if (fLoc && m.location_id !== fLoc) return false;
      if (mType && m.move_type !== mType) return false;
      if (mFrom && m.move_date < mFrom) return false;
      if (mTo && m.move_date > mTo) return false;
      if (s) { const it = ctx.items.find(i => i.id === m.item_id); if (!((it ? it.name : '') + ' ' + (m.shop_name || '') + ' ' + (m.issued_to_name || '') + ' ' + (m.bill_no || '')).toLowerCase().includes(s)) return false; }
      return true;
    });
  }, [ctx.moves, ctx.items, q, fLoc, mType, mFrom, mTo]);

  function exportStock() {
    const out = rows.map((r, i) => {
      const o = { '#': i + 1, 'Item': r.item.name, 'Category': r.item.category || '', 'Unit': r.item.unit, 'Department': r.item.department || '' };
      r.per.forEach(p => { o[p.loc.name] = p.bal; });
      o['Total'] = r.per.reduce((t, p) => t + p.bal, 0); o['Minimum'] = num(r.item.min_stock);
      const li = lastIn[r.item.id]; o['Last bought'] = li ? li.move_date : ''; o['Last rate'] = li && li.rate != null ? li.rate : ''; o['Last shop'] = li ? (li.shop_name || ctx.vendorName(li.vendor_id) || '') : '';
      return o;
    });
    if (out.length) saveStyledXlsx('Consumable_Stock_' + todayStr() + '.xlsx', [{ name: 'Stock', title: 'Consumable Stock', rows: out }]);
  }
  function exportReorder() {
    const out = lowRows.map((r, i) => {
      const li = lastIn[r.item.id]; const min = num(r.item.min_stock);
      return { '#': i + 1, 'Item': r.item.name, 'Category': r.item.category || '', 'Unit': r.item.unit, 'Balance': r.bal, 'Minimum': min, 'Suggested order qty': Math.max(1, Math.ceil(min * 2 - r.bal)), 'Last shop': li ? (li.shop_name || ctx.vendorName(li.vendor_id) || '') : '', 'Last rate': li && li.rate != null ? li.rate : '' };
    });
    if (out.length) saveStyledXlsx('Reorder_List_' + todayStr() + '.xlsx', [{ name: 'Reorder', title: 'Reorder List', rows: out }]);
  }
  function exportMoves() {
    const out = moves.map((m, i) => { const it = ctx.items.find(x => x.id === m.item_id); return { '#': i + 1, 'Date': m.move_date, 'Item': it ? it.name : '', 'Location': ctx.locName(m.location_id), 'Type': MOVE_LABEL[m.move_type], 'Qty': m.qty, 'Rate': m.rate ?? '', 'Amount': m.amount ?? '', 'Shop': m.shop_name || ctx.vendorName(m.vendor_id) || '', 'Bill No': m.bill_no || '', 'Issued To': m.issued_to_name || '', 'Department': m.department || '', 'Purpose': m.purpose || '', 'Notes': m.notes || '' }; });
    if (out.length) saveStyledXlsx('Consumable_Movements_' + todayStr() + '.xlsx', [{ name: 'Movements', title: 'Consumable Movements', rows: out }]);
  }
  async function delMove(m) {
    if (!ctx.canEditLoc(m.location_id)) return toast.error('Not allowed for this location');
    const pair = m.transfer_ref ? ' (both sides of the transfer will be removed)' : '';
    if (!window.confirm('Delete this stock entry' + pair + '?')) return;
    const { error } = m.transfer_ref ? await supabase.from('consumable_moves').delete().eq('transfer_ref', m.transfer_ref) : await supabase.from('consumable_moves').delete().eq('id', m.id);
    if (error) return toast.error(error.message);
    await removeFiles(m.attachments); ctx.reload();
  }

  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const mRows = moves.slice((mPage - 1) * PAGE_SIZE, mPage * PAGE_SIZE);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <div className="search-bar" style={{ width: 220 }}><Search size={14} /><input placeholder="Search item, shop…" value={q} onChange={e => setQ(e.target.value)} /></div>
        <select style={{ width: 160 }} value={fLoc} onChange={e => setFLoc(e.target.value)}><option value="">All locations</option>{activeLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        {view === 'stock' && <>
          <select style={{ width: 150 }} value={fCat} onChange={e => setFCat(e.target.value)}><option value="">All categories</option>{CONS_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          <select style={{ width: 130 }} value={fDept} onChange={e => setFDept(e.target.value)}><option value="">All depts</option>{DEPARTMENTS.map(d => <option key={d}>{d}</option>)}</select>
          <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} />Low / out only</label>
        </>}
        {view === 'moves' && <>
          <select style={{ width: 140 }} value={mType} onChange={e => setMType(e.target.value)}><option value="">All types</option>{Object.keys(MOVE_LABEL).map(k => <option key={k} value={k}>{MOVE_LABEL[k]}</option>)}</select>
          <DateInput value={mFrom} onChange={e => setMFrom(e.target.value)} /><span style={{ fontSize: 12 }}>to</span><DateInput value={mTo} onChange={e => setMTo(e.target.value)} />
        </>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${view === 'stock' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('stock')}>Stock</button>
          <button className={`btn btn-sm ${view === 'moves' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('moves')}>Movements</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {ctx.canAddAny && <>
          <button className="btn btn-primary btn-sm" onClick={() => setDlg({ mode: 'IN', loc: fLoc })}>📥 Stock in</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setDlg({ mode: 'OUT', loc: fLoc })}>📤 Issue</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setDlg({ mode: 'TRANSFER', loc: fLoc })}>🔁 Transfer</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setDlg({ mode: 'ADJUST', loc: fLoc })}>🛠 Correct stock</button>
        </>}
        {ctx.isManager && <button className="btn btn-secondary btn-sm" onClick={() => setItemForm({ editing: null })}><Plus size={13} /> New item</button>}
        <button className="btn btn-secondary btn-sm" onClick={view === 'stock' ? exportStock : exportMoves}><Download size={13} /> Excel</button>
        {view === 'stock' && lowRows.length > 0 && <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }} onClick={exportReorder}>🛒 Reorder list ({lowRows.length})</button>}
      </div>

      {view === 'stock' && <>
        {lowRows.length > 0 && <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', fontSize: 12, marginBottom: 10 }}>⚠️ <b>{lowRows.length}</b> item(s) at or below minimum stock{fLoc ? ' at ' + ctx.locName(fLoc) : ''}: {lowRows.slice(0, 8).map(r => r.item.name).join(', ')}{lowRows.length > 8 ? '…' : ''}</div>}
        <div className="table-wrap">
          <table style={{ minWidth: 900 }}>
            <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>{fLoc ? 'Balance' : 'Total balance'}</th>{!fLoc && <th>By location</th>}<th style={{ textAlign: 'right' }}>Min</th><th>Last purchase</th><th /></tr></thead>
            <tbody>
              {pageRows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>{ctx.items.length === 0 ? 'No items yet – a manager can add items with “New item”.' : 'No items match.'}</td></tr>}
              {pageRows.map(r => { const li = lastIn[r.item.id]; return (
                <tr key={r.item.id} style={{ cursor: 'pointer' }} onClick={() => setViewItem(r.item.id)}>
                  <td><div style={{ fontWeight: 600, color: 'var(--accent)' }}>{r.item.name}</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>{[r.item.category, r.item.department].filter(Boolean).join(' · ')}</div></td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: r.out ? 'var(--red)' : r.low ? '#b45309' : 'var(--text)' }}>{r.bal} <span style={{ fontWeight: 400, fontSize: 11 }}>{r.item.unit}</span>{(r.out || r.low) && num(r.item.min_stock) > 0 && <div><Chip bg={r.out ? '#fee2e2' : '#fef3c7'} color={r.out ? '#991b1b' : '#92400e'}>{r.out ? 'OUT' : 'LOW'}</Chip></div>}</td>
                  {!fLoc && <td style={{ fontSize: 11 }}>{r.per.map(p => <span key={p.loc.id} style={{ marginRight: 8, color: p.bal <= 0 ? 'var(--text3)' : 'var(--text)' }}>{p.loc.name.split(' ')[0]}: <b>{p.bal}</b></span>)}</td>}
                  <td style={{ textAlign: 'right' }}>{num(r.item.min_stock) || '—'}</td>
                  <td style={{ fontSize: 11 }}>{li ? <>{fmtDate(li.move_date)}<div style={{ color: 'var(--text3)' }}>{[li.shop_name || ctx.vendorName(li.vendor_id), li.rate != null ? '@' + inr(li.rate) : ''].filter(Boolean).join(' · ')}</div></> : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    {ctx.canAddAny && <button className="btn btn-ghost btn-sm" title="Stock in" onClick={() => setDlg({ mode: 'IN', item: r.item.id, loc: fLoc })}>📥</button>}
                    {ctx.canAddAny && <button className="btn btn-ghost btn-sm" title="Issue" onClick={() => setDlg({ mode: 'OUT', item: r.item.id, loc: fLoc })}>📤</button>}
                    {ctx.isManager && <button className="btn btn-ghost btn-sm" title="Edit item" onClick={() => setItemForm({ editing: r.item })}><Edit2 size={13} /></button>}
                  </td>
                </tr>); })}
            </tbody>
          </table>
        </div>
        <Pager page={page} setPage={setPage} total={rows.length} />
      </>}

      {view === 'moves' && <>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{moves.length} entries{ctx.moves.length >= 3000 ? ' (showing the latest 3000)' : ''}</div>
        <div className="table-wrap">
          <table style={{ minWidth: 900 }}>
            <thead><tr><th>Date</th><th>Item</th><th>Location</th><th>Type</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Amount</th><th>Details</th><th /></tr></thead>
            <tbody>
              {mRows.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>No entries.</td></tr>}
              {mRows.map(m => { const it = ctx.items.find(x => x.id === m.item_id); return (
                <tr key={m.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.move_date)}</td><td style={{ fontWeight: 600 }}>{it ? it.name : '—'}</td><td>{ctx.locName(m.location_id)}</td>
                  <td><span style={{ background: MOVE_COLOR[m.move_type][0], color: MOVE_COLOR[m.move_type][1], borderRadius: 8, padding: '1px 7px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{MOVE_LABEL[m.move_type]}</span></td>
                  <td style={{ textAlign: 'right' }}>{m.qty} {it ? it.unit : ''}</td><td style={{ textAlign: 'right' }}>{m.amount != null ? inr(m.amount) : '—'}</td>
                  <td style={{ fontSize: 11, maxWidth: 320 }}>{m.move_type === 'IN' ? [m.shop_name || ctx.vendorName(m.vendor_id), m.rate != null ? '@' + inr(m.rate) : '', m.bill_no ? 'bill ' + m.bill_no : '', m.accounts_entry_id ? '✓ Accounts' : ''].filter(Boolean).join(' · ') : [m.issued_to_name, m.department, m.purpose, m.notes].filter(Boolean).join(' · ')}</td>
                  <td>{ctx.canEditLoc(m.location_id) && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => delMove(m)}><Trash2 size={12} /></button>}</td>
                </tr>); })}
            </tbody>
          </table>
        </div>
        <Pager page={mPage} setPage={setMPage} total={moves.length} />
      </>}

      {dlg && <MoveModal ctx={ctx} mode={dlg.mode} presetItem={dlg.item} presetLoc={dlg.loc} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); ctx.reload(); }} />}
      {itemForm && <ItemForm ctx={ctx} editing={itemForm.editing} onClose={() => setItemForm(null)} onSaved={() => { setItemForm(null); ctx.reload(); }} />}
      {viewItem && <ItemView ctx={ctx} itemId={viewItem} onClose={() => setViewItem(null)} onEdit={it => { setViewItem(null); setItemForm({ editing: it }); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  DASHBOARD
// ═══════════════════════════════════════════════════════════════════
function Bars({ rows, color = '#00756a', fmt = v => v, onClick }) {
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <div>
      {rows.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>No data.</div>}
      {rows.map(r => (
        <div key={r.label} onClick={onClick ? () => onClick(r) : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, cursor: onClick ? 'pointer' : 'default', fontSize: 12 }}>
          <div style={{ width: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.label}>{r.label}</div>
          <div style={{ flex: 1, background: 'var(--bg2, #f3f4f6)', borderRadius: 4, height: 12 }}><div style={{ width: (r.value / max * 100) + '%', background: color, height: 12, borderRadius: 4 }} /></div>
          <div style={{ width: 90, textAlign: 'right', fontWeight: 600 }}>{fmt(r.value)}</div>
        </div>
      ))}
    </div>
  );
}
const Panel = ({ title, children }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, background: '#fff', minWidth: 0 }}>
    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>{title}</div>{children}
  </div>
);

function DashboardTab({ ctx, onGo }) {
  const live = ctx.assets.filter(a => a.status !== 'Disposed' && a.status !== 'Lost');
  const msum = useMemo(() => summarizeMaint(ctx.maint), [ctx.maint]);
  const units = a => (a.tracking === 'group' ? Math.max(1, num(a.quantity)) : 1);
  const totalUnits = live.reduce((t, a) => t + units(a), 0);
  const value = live.reduce((t, a) => t + num(a.purchase_amount), 0);
  const book = live.reduce((t, a) => t + (bookValue(a) ?? num(a.purchase_amount)), 0);
  const bad = live.filter(a => a.status === 'Damaged' || a.status === 'Under repair');
  const warn = live.filter(a => { const d = daysTo(a.warranty_expiry); return d !== null && d >= 0 && d <= EXPIRY_SOON; });
  const svc = live.filter(a => { const s = msum[a.id]; const d = s && s.nextDue ? daysTo(s.nextDue) : null; return d !== null && d <= EXPIRY_SOON; });
  const heavy = live.filter(a => { const s = msum[a.id]; return s && num(a.purchase_amount) > 0 && s.cost > num(a.purchase_amount) * REPLACE_RATIO; });
  const month = todayStr().slice(0, 7);
  const maintMonth = ctx.maint.filter(m => String(m.maint_date || '').slice(0, 7) === month).reduce((t, m) => t + num(m.cost), 0);
  const consMonth = ctx.moves.filter(m => m.move_type === 'IN' && String(m.move_date || '').slice(0, 7) === month).reduce((t, m) => t + num(m.amount), 0);
  const lowItems = ctx.items.filter(i => i.is_active !== false && num(i.min_stock) > 0).filter(i => ctx.locations.some(l => l.is_active !== false && ctx.stockMap[stockKey(i.id, l.id)] && num(ctx.stockMap[stockKey(i.id, l.id)].balance) <= num(i.min_stock)));

  const group = (keyFn, valFn) => { const m = {}; live.forEach(a => { const k = keyFn(a) || 'Unassigned'; m[k] = (m[k] || 0) + valFn(a); }); return Object.entries(m).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value); };
  const byDept = group(a => a.department, units);
  const byLoc = group(a => ctx.locName(a.location_id), units);
  const byStatus = group(a => a.status, units);
  const topMaint = Object.entries(msum).map(([id, s]) => ({ a: ctx.assets.find(x => x.id === id), s })).filter(r => r.a && r.s.cost > 0).sort((a, b) => b.s.cost - a.s.cost).slice(0, 6);

  const List = ({ rows, empty }) => rows.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>{empty}</div> : <div>{rows}</div>;
  const li = (key, left, right) => <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border)' }}><span>{left}</span><span style={{ color: 'var(--text3)', whiteSpace: 'nowrap' }}>{right}</span></div>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 10, marginBottom: 14 }}>
        <Stat label="Assets in use (units)" value={totalUnits} sub={live.length + ' records'} onClick={() => onGo('assets', {})} />
        <Stat label="Purchase value" value={inr(value)} color="#2563eb" />
        <Stat label="Book value (depreciated)" value={inr(book)} color="#7c3aed" />
        <Stat label="Damaged / under repair" value={bad.length} color="#dc2626" onClick={() => onGo('assets', { status: '__bad' })} />
        <Stat label={`Warranty ending ≤${EXPIRY_SOON}d`} value={warn.length} color="#d97706" onClick={() => onGo('assets', { warranty: true })} />
        <Stat label="Service due" value={svc.length} color="#d97706" onClick={() => onGo('assets', { service: true })} />
        <Stat label="Repair-heavy (consider replacing)" value={heavy.length} color="#dc2626" onClick={() => onGo('assets', { heavy: true })} />
        <Stat label="Low-stock items" value={lowItems.length} color="#dc2626" onClick={() => onGo('consumables', { low: true })} />
        <Stat label="Maintenance this month" value={inr(maintMonth)} color="#0891b2" />
        <Stat label="Consumables bought this month" value={inr(consMonth)} color="#0891b2" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12 }}>
        <Panel title="Units by department"><Bars rows={byDept} onClick={r => onGo('assets', { department: r.label === 'Unassigned' ? '' : r.label })} /></Panel>
        <Panel title="Units by location"><Bars rows={byLoc} color="#2563eb" /></Panel>
        <Panel title="Units by status"><Bars rows={byStatus} color="#d97706" /></Panel>
        <Panel title="Warranty expiring soon"><List empty="Nothing expiring." rows={warn.sort((a, b) => a.warranty_expiry.localeCompare(b.warranty_expiry)).slice(0, 8).map(a => li(a.id, a.name + ' · ' + ctx.locName(a.location_id), fmtDate(a.warranty_expiry)))} /></Panel>
        <Panel title="Service due"><List empty="No service due." rows={svc.slice(0, 8).map(a => li(a.id, a.name + ' · ' + ctx.locName(a.location_id), fmtDate(msum[a.id].nextDue)))} /></Panel>
        <Panel title="Damaged / under repair"><List empty="All good." rows={bad.slice(0, 8).map(a => li(a.id, a.name + ' · ' + ctx.locName(a.location_id), a.status))} /></Panel>
        <Panel title="Low stock"><List empty="Stock is healthy." rows={lowItems.slice(0, 8).map(i => li(i.id, i.name, 'min ' + i.min_stock + ' ' + i.unit))} /></Panel>
        <Panel title="Highest maintenance cost"><List empty="No maintenance recorded." rows={topMaint.map(r => li(r.a.id, r.a.name + (heavy.includes(r.a) ? ' ⚠ replace?' : ''), inr(r.s.cost) + ' / ' + inr(r.a.purchase_amount)))} /></Panel>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  VENDORS
// ═══════════════════════════════════════════════════════════════════
function VendorForm({ editing, onClose, onSaved }) {
  const [f, setF] = useState(() => editing ? { name: editing.name || '', phone: editing.phone || '', gst_no: editing.gst_no || '', address: editing.address || '', items_supplied: editing.items_supplied || '', notes: editing.notes || '', is_active: editing.is_active !== false } : { name: '', phone: '', gst_no: '', address: '', items_supplied: '', notes: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const sf = (k, v) => setF(p => ({ ...p, [k]: v }));
  async function save() {
    if (!f.name.trim()) return toast.error('Name is required');
    setSaving(true);
    const body = { ...f, name: f.name.trim() };
    const { error } = editing ? await supabase.from('asset_vendors').update(body).eq('id', editing.id) : await supabase.from('asset_vendors').insert(body);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success('Saved'); onSaved();
  }
  return (
    <Modal title={editing ? 'Edit vendor' : 'Add vendor / shop'} onClose={onClose} width={600}>
      <div className="form-grid">
        <FG label="Shop / vendor name *" full><input value={f.name} onChange={e => sf('name', e.target.value)} /></FG>
        <FG label="Phone"><input value={f.phone} onChange={e => sf('phone', e.target.value)} /></FG>
        <FG label="GST no."><input value={f.gst_no} onChange={e => sf('gst_no', e.target.value)} /></FG>
        <FG label="Address" full><input value={f.address} onChange={e => sf('address', e.target.value)} /></FG>
        <FG label="Supplies" full><input value={f.items_supplied} onChange={e => sf('items_supplied', e.target.value)} placeholder="e.g. laptops, stationery, welding items" /></FG>
        <FG label="Notes" full><input value={f.notes} onChange={e => sf('notes', e.target.value)} /></FG>
        {editing && <FG label="Active"><select value={f.is_active ? 'y' : 'n'} onChange={e => sf('is_active', e.target.value === 'y')}><option value="y">Active</option><option value="n">Inactive</option></select></FG>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

function VendorsTab({ ctx }) {
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null);
  const spend = useMemo(() => {
    const m = {};
    const add = (id, name, v) => { const k = id || ('n:' + (name || '').toLowerCase()); if (!k || k === 'n:') return; m[k] = (m[k] || 0) + num(v); };
    ctx.assets.forEach(a => add(a.vendor_id, a.shop_name, a.purchase_amount));
    ctx.moves.forEach(x => { if (x.move_type === 'IN') add(x.vendor_id, x.shop_name, x.amount); });
    ctx.maint.forEach(x => add(x.vendor_id, x.shop_name, x.cost));
    return m;
  }, [ctx.assets, ctx.moves, ctx.maint]);
  const rows = ctx.vendors.filter(v => !q.trim() || (v.name + ' ' + (v.items_supplied || '') + ' ' + (v.phone || '')).toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ width: 240 }}><Search size={14} /><input placeholder="Search vendor…" value={q} onChange={e => setQ(e.target.value)} /></div>
        {ctx.canAddAny && <button className="btn btn-primary btn-sm" onClick={() => setForm({ editing: null })}><Plus size={13} /> Add vendor</button>}
      </div>
      <div className="table-wrap">
        <table style={{ minWidth: 700 }}>
          <thead><tr><th>Vendor / shop</th><th>Phone</th><th>Supplies</th><th>GST no.</th><th style={{ textAlign: 'right' }}>Total spend</th><th /></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>No vendors yet.</td></tr>}
            {rows.map(v => (
              <tr key={v.id} style={{ opacity: v.is_active === false ? 0.5 : 1 }}>
                <td style={{ fontWeight: 600 }}>{v.name}</td><td>{v.phone || '—'}</td><td style={{ fontSize: 12 }}>{v.items_supplied || '—'}</td><td>{v.gst_no || '—'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{inr(spend[v.id] || spend['n:' + v.name.toLowerCase()] || 0)}</td>
                <td>{ctx.canAddAny && <button className="btn btn-ghost btn-sm" onClick={() => setForm({ editing: v })}><Edit2 size={13} /></button>}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {form && <VendorForm editing={form.editing} onClose={() => setForm(null)} onSaved={() => { setForm(null); ctx.reload(); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  SETTINGS  (locations + in-charge staff)
// ═══════════════════════════════════════════════════════════════════
function SettingsTab({ ctx }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('Office');
  async function addLoc() {
    if (!name.trim()) return toast.error('Enter a location name');
    const { error } = await supabase.from('asset_locations').insert({ name: name.trim(), kind });
    if (error) return toast.error(/duplicate|unique/i.test(error.message) ? 'Location already exists' : error.message);
    setName(''); ctx.reload();
  }
  async function setIncharge(loc, ids) {
    const { error } = await supabase.from('asset_locations').update({ incharge_ids: ids }).eq('id', loc.id);
    if (error) return toast.error(error.message);
    ctx.reload();
  }
  async function toggle(loc) {
    const { error } = await supabase.from('asset_locations').update({ is_active: loc.is_active === false }).eq('id', loc.id);
    if (error) return toast.error(error.message);
    ctx.reload();
  }
  if (!ctx.isManager) return <div style={{ padding: 30, color: 'var(--text3)' }}>Only managers can change settings.</div>;
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>Each location can have one or more in-charge staff. In-charge staff (and managers) can change that location’s assets and stock. If no in-charge is set anywhere, everyone with edit access can edit.</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input style={{ width: 220 }} placeholder="New location name" value={name} onChange={e => setName(e.target.value)} />
        <select style={{ width: 120 }} value={kind} onChange={e => setKind(e.target.value)}><option>Office</option><option>Site</option><option>Store</option></select>
        <button className="btn btn-primary btn-sm" onClick={addLoc}><Plus size={13} /> Add location</button>
      </div>
      {ctx.locations.map(l => {
        const ids = (l.incharge_ids || []).map(String);
        return (
          <div key={l.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 10, background: '#fff', opacity: l.is_active === false ? 0.55 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 700 }}>{l.name} <Chip bg="#e5e7eb" color="#374151">{l.kind}</Chip></div>
              <button className="btn btn-ghost btn-sm" onClick={() => toggle(l)}>{l.is_active === false ? 'Activate' : 'Deactivate'}</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>In-charge staff</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              {ids.length === 0 && <span style={{ fontSize: 12, color: 'var(--text3)' }}>None set</span>}
              {ids.map(id => <span key={id} style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 10, padding: '2px 8px', fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center' }}>{ctx.staffName(id)}<X size={11} style={{ cursor: 'pointer' }} onClick={() => setIncharge(l, ids.filter(x => x !== id))} /></span>)}
            </div>
            <select style={{ width: 240 }} value="" onChange={e => { if (e.target.value) setIncharge(l, [...ids, e.target.value]); }}>
              <option value="">+ Add in-charge…</option>
              {ctx.staff.filter(s => !ids.includes(String(s.id))).map(s => <option key={s.id} value={String(s.id)}>{s.full_name}</option>)}
            </select>
          </div>);
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  MAIN
// ═══════════════════════════════════════════════════════════════════
export default function AssetsConsumables() {
  const { user, canView, canEdit } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [data, setData] = useState({ locations: [], vendors: [], staff: [], assets: [], maint: [], items: [], stock: [], moves: [], payModes: [] });
  const [myStaffId, setMyStaffId] = useState(null);
  const [assetPreset, setAssetPreset] = useState(null);
  const [consPreset, setConsPreset] = useState(null);

  async function load() {
    const q = (p) => p.then(r => r, e => ({ data: null, error: e }));
    const [loc, ven, stf, ast, mnt, itm, stk, mov, pm] = await Promise.all([
      q(supabase.from('asset_locations').select('*').order('name')),
      q(supabase.from('asset_vendors').select('*').order('name')),
      q(supabase.from('staff').select('id,full_name,user_id,is_active').order('full_name')),
      q(supabase.from('assets').select('*').eq('is_active', true).order('created_at', { ascending: false }).limit(5000)),
      q(supabase.from('asset_maintenance').select('id,asset_id,maint_date,maint_type,cost,next_due,vendor_id,shop_name').limit(10000)),
      q(supabase.from('consumable_items').select('*').order('name')),
      q(supabase.from('consumable_stock').select('*').limit(10000)),
      q(supabase.from('consumable_moves').select('*').order('move_date', { ascending: false }).order('created_at', { ascending: false }).limit(3000)),
      q(supabase.from('accounts_payment_modes').select('name').limit(100)),
    ]);
    if ([loc, ast, itm, mov].some(r => isMissingTable(r.error))) { setMissing(true); setLoading(false); return; }
    setMissing(false);
    const staffAll = stf.data || [];
    const modes = (pm.data || []).map(r => r.name).filter(Boolean);
    setData({
      locations: loc.data || [], vendors: ven.data || [], staff: staffAll.filter(s => s.is_active !== false),
      assets: ast.data || [], maint: mnt.data || [], items: itm.data || [], stock: stk.data || [], moves: mov.data || [],
      payModes: modes.length ? modes : PAY_MODES_FALLBACK, staffAll,
    });
    const me = staffAll.find(s => user && s.user_id === user.id);
    setMyStaffId(me ? String(me.id) : null);
    setLoading(false);
  }
  useEffect(() => { load(); }, []); // eslint-disable-line
  useLiveRefresh(['assets', 'asset_maintenance', 'asset_locations', 'asset_vendors', 'consumable_items', 'consumable_stock', 'consumable_moves'], () => load(), true, 1500);

  const ctx = useMemo(() => {
    const stockMap = {};
    data.stock.forEach(s => { stockMap[stockKey(s.item_id, s.location_id)] = s; });
    const isManager = !!(canEdit('assets_settings') || canEdit('staff_admin'));
    const anyIncharge = data.locations.some(l => (l.incharge_ids || []).length > 0);
    const canEditLoc = locId => {
      if (!canEdit('assets')) return false;
      if (isManager || !anyIncharge) return true;
      const l = data.locations.find(x => x.id === locId);
      return !!(l && myStaffId && (l.incharge_ids || []).map(String).includes(String(myStaffId)));
    };
    const byId = (list, id) => list.find(x => String(x.id) === String(id));
    return {
      ...data, stockMap, isManager, myStaffId, canEditLoc,
      canAddAny: canEdit('assets') && (isManager || !anyIncharge || data.locations.some(l => canEditLoc(l.id))),
      reload: load,
      locName: id => { const l = byId(data.locations, id); return l ? l.name : '—'; },
      vendorName: id => { const v = byId(data.vendors, id); return v ? v.name : ''; },
      staffName: id => { const s = byId(data.staffAll || data.staff, id); return s ? s.full_name : '—'; },
    };
  }, [data, myStaffId, canEdit]); // eslint-disable-line

  const tabs = [
    { key: 'dashboard', label: '📊 Dashboard', perm: 'assets_dashboard' },
    { key: 'assets', label: '🖥️ Assets', perm: 'assets_assets' },
    { key: 'consumables', label: '📦 Consumables', perm: 'assets_consumables' },
    { key: 'vendors', label: '🏪 Vendors', perm: 'assets_vendors' },
    { key: 'settings', label: '⚙️ Settings', perm: 'assets_settings' },
  ].filter(t => canView(t.perm));
  const activeTab = tabs.find(t => t.key === tab) ? tab : (tabs[0] && tabs[0].key);

  function go(t, preset) { if (t === 'assets') setAssetPreset(preset || {}); if (t === 'consumables') setConsPreset(preset || {}); setTab(t); }

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (missing) return (
    <div style={{ padding: 30 }}>
      <div className="page-title">Assets & Consumables</div>
      <div style={{ marginTop: 16, padding: 16, borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 13 }}>
        The database tables for this section are not created yet. Run <b>assets_setup.sql</b> once in the Supabase SQL editor, then reload this page.
      </div>
    </div>
  );
  return (
    <div>
      <div className="page-header"><div><div className="page-title">Assets & Consumables</div><div style={{ fontSize: 12, color: 'var(--text3)' }}>Company assets, maintenance, stationery and stock</div></div></div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
        {tabs.map(t => <button key={t.key} className={`btn btn-sm ${activeTab === t.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>
      {activeTab === 'dashboard' && <DashboardTab ctx={ctx} onGo={go} />}
      {activeTab === 'assets' && <AssetsTab ctx={ctx} preset={assetPreset} clearPreset={() => setAssetPreset(null)} />}
      {activeTab === 'consumables' && <ConsumablesTab ctx={ctx} preset={consPreset} clearPreset={() => setConsPreset(null)} />}
      {activeTab === 'vendors' && <VendorsTab ctx={ctx} />}
      {activeTab === 'settings' && <SettingsTab ctx={ctx} />}
    </div>
  );
}
